/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import Image from 'next/legacy/image'
import * as Tabs from '@radix-ui/react-tabs'
import { useSwipeable } from 'react-swipeable'
import s from './style.module.css'

interface ItemProps {
	icon: {
		src: string
	}
	label: string
	benefits: Array<{
		text: string
	}>
}

export interface BenefitsProps {
	heading: string
	items: Array<ItemProps>
}

export default function Benefits({
	heading,
	items,
}: BenefitsProps): JSX.Element {
	const [activeTabIndex, setActiveTabIndex] = useState('0')
	const prevTabIndex = usePrevious(activeTabIndex)
	const swipeHandlers = useSwipeable({
		onSwiped: ({ dir }) => handleSwipe(dir),
	})

	const handleTabsChange = (index) => setActiveTabIndex(index)
	const handleSwipe = (dir) => {
		if (dir === 'Right') {
			if (activeTabIndex === '0') {
				return
			} else {
				setActiveTabIndex((prevIndex) =>
					(parseInt(prevIndex, 10) - 1).toString(),
				)
			}
		} else if (dir === 'Left') {
			if (parseInt(activeTabIndex, 10) >= items.length - 1) {
				return
			} else {
				setActiveTabIndex((prevIndex) =>
					(parseInt(prevIndex, 10) + 1).toString(),
				)
			}
		}
	}

	const [isStacked, setIsStacked] = useState(
		typeof window !== 'undefined'
			? window.matchMedia('(max-width: 768px)').matches
			: true,
	)

	useEffect(() => {
		const handleResize = () => {
			setIsStacked(window.matchMedia('(max-width: 768px)').matches)
		}
		window.addEventListener('resize', handleResize)
		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [isStacked])

	return (
		<div className={s.root}>
			<div className={s.container}>
				<h2 className={s.heading}>{heading}</h2>
				<Tabs.Root
					className={s.tabs}
					value={activeTabIndex}
					onValueChange={handleTabsChange}
					orientation={isStacked ? 'horizontal' : 'vertical'}
				>
					<Tabs.List className={s.tabList}>
						{items.map((item, index) => {
							return (
								// Index is stable

								<Tabs.Trigger
									className={s.tab}
									key={index}
									value={index.toString()}
								>
									<span className={s.tabIcon}>
										<Image src={item.icon.src} width={28} height={28} alt="" />
									</span>
									<span className={s.tabLabel}>{item.label}</span>
								</Tabs.Trigger>
							)
						})}
					</Tabs.List>

					{/*
            These labels are purely visual since it duplicates the content in the <Tab /> buttons.
            So we hide them from being read to screen readers using aria-hidden.
          */}
					<div className={s.tabsLabels} aria-hidden={true}>
						{items.map((item, index) => {
							return (
								<p
									// Index is stable
									// eslint-disable-next-line react/no-array-index-key
									key={index}
									className={classNames(
										s.tabsLabelsItem,
										activeTabIndex === index.toString() && s.active,
									)}
								>
									<span className={s.tabsLabelsIcon}>
										<Image
											src={item.icon.src}
											width={28}
											height={28}
											alt=""
										/>{' '}
									</span>
									<span>{item.label}</span>
								</p>
							)
						})}
					</div>

					<div
						className={s.tabPanelsContainer}
						{...swipeHandlers}
						style={{ touchAction: 'pan-y' }}
					>
						<LayeredBackground />
						<div className={s.tabPanels}>
							{items.map((item, index) => {
								return (
									<Tabs.Content
										key={item.label}
										value={index.toString()}
										forceMount
										className={classNames(s.tabPanel, {
											[s.slideInLeft]:
												activeTabIndex === index.toString() &&
												parseInt(activeTabIndex, 10) >
													parseInt(prevTabIndex, 0),
											[s.slideInRight]:
												activeTabIndex === index.toString() &&
												parseInt(activeTabIndex, 10) <
													parseInt(prevTabIndex, 0),
											[s.slideOutLeft]:
												prevTabIndex === index.toString() &&
												parseInt(activeTabIndex, 10) <
													parseInt(prevTabIndex, 0),
											[s.slideOutRight]:
												prevTabIndex === index.toString() &&
												parseInt(activeTabIndex, 10) >
													parseInt(prevTabIndex, 0),
										})}
									>
										<ul className={s.tabPanelList}>
											{item.benefits.map((benefit, index) => {
												return (
													// Index is stable
													// eslint-disable-next-line react/no-array-index-key
													<li className={s.tabPanelItem} key={index}>
														<span>
															<Image
																src="/img/industries/checkmark.svg"
																width={24}
																height={24}
																alt=""
															/>
														</span>
														{benefit.text}
													</li>
												)
											})}
										</ul>
									</Tabs.Content>
								)
							})}
						</div>
					</div>
				</Tabs.Root>
			</div>
		</div>
	)
}

function LayeredBackground(): JSX.Element {
	return (
		<>
			{Array.from(Array(4).keys()).map((_, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<span key={index} className={s.tabPanelsBackground} />
			))}
		</>
	)
}

function usePrevious<T>(value: T): T {
	const ref = useRef<T | null>(null)
	useEffect(() => {
		ref.current = value
	})
	return ref.current
}
